import { FC, ReactElement, useCallback, useEffect, useId, useState } from 'react';

import { Tooltip } from '@pp/core/src/components/tooltip/Tooltip';
import { useResizeEffect } from '@pp/core/src/hooks/useResizeEffect';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { GameStatus } from '@pp/core/src/store/slices/game/types';
import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';

import { useRecentResultFetcher } from '../../../store/apiHooks/useRecentResultFetcher';
import { useHighflyerGameStateSlice } from '../../../store/slices/highflyerGame/selectors';
import { useIsSmallerThanHeight280AndLandscape } from '../../../styles/hooks/media';
import { SingleResult } from '../singleResult';

import styles from './InlineRecentResults.module.css';

const calculateVisibleResults = (): number => {
  const width = window.innerWidth;
  return Math.ceil(width / 26);
};

export const InlineRecentResultsConnected: FC = (): ReactElement | null => {
  const id = useId();
  const [visibleCount, setVisibleCount] = useState(calculateVisibleResults());
  const [hideTooltip, setHideTooltip] = useState(false);

  const showInPlayTimerButton = useHighflyerGameStateSlice.timerInPlayBtnStatus();
  const betsOpenStatus = useGameStateSlice.getCurrentStatus();
  const isGameCancelled = betsOpenStatus === GameStatus.GameCancelled;
  const isHeightLessThan280AndLandscape = useIsSmallerThanHeight280AndLandscape();

  useEffect(() => {
    if (!isGameCancelled) {
      setHideTooltip(false);
    }
  }, [isGameCancelled]);

  const recentResultFetcher = useRecentResultFetcher();
  useEffect(() => {
    recentResultFetcher();
  }, []);

  const recentResults = useHighflyerGameStateSlice.recentResults()?.history;

  useResizeEffect(
    useCallback(() => {
      const count = calculateVisibleResults();
      setVisibleCount(count);
    }, [])
  );

  const showInPlayMods = useModifiers(styles, 'inPlayButton', {
    show: showInPlayTimerButton,
    hide: !showInPlayTimerButton,
  });

  if (isHeightLessThan280AndLandscape) {
    return null;
  }

  return (
    <div data-testid="inline-recent-results" className={styles.InlineRecentResults}>
      <Tooltip
        text={'GAMECANCELLED'}
        disabled={!isGameCancelled || hideTooltip}
        displayTrigger={isGameCancelled}
        displayTimer={3000}
        displayOnTouch={true}
      >
        <div className={showInPlayMods}>
          <SingleResult key={-1} type={-1} value={-1} />
        </div>
      </Tooltip>

      <div className={`${styles.recentResultBtn} ${showInPlayTimerButton && styles.inPlayButton_show}`}>
        {recentResults &&
          recentResults.slice(0, visibleCount).map((result, index) => (
            <div key={`recentResults-${id}`} className={styles.singleButton}>
              <SingleResult active={index === 0} type={result.gameResult} value={result.gameResult} />
            </div>
          ))}
      </div>
      <div className={styles.dullAppearance} />
    </div>
  );
};
